import { useEffect, useRef, useState } from "react";
import { useNavigate, generatePath } from "react-router-dom";
import {
  useGetUserWorkoutHistoryQuery,
  useHardDeleteWorkoutMutation,
  useLazyGetWorkoutQuery,
} from "../../libs/workout/api";
import { Workout } from "../../libs/workout/types";
import {
  captureElementsToPdf,
  sanitizeFileName,
} from "../../libs/workout/pdfImages";
import { useAppSelector } from "../../store";
import { selectGetUserResult } from "../../libs/user/selectors";
import { ROUTES } from "../../constants/routes";
import { DeleteButton } from "../Button";
import { Modal } from "../Modal";
import { WorkoutTables } from "../WorkoutTables";
import { WorkoutViewModal } from "./WorkoutViewModal";

type Props = {
  userId?: string;
};

type WorkoutHistoryItem = {
  id: string;
  name?: string;
  workoutType?: "DEFAULT" | "TEMPLATE";
  createdAt?: number | null;
  deletedAt?: number | null;
};

export const WorkoutHistoryTable: React.FC<Props> = ({ userId }) => {
  // Lo storico usa un endpoint dedicato che include anche le schede
  // soft-deleted (con badge "Cancellata"). user.workouts qui non basta perche'
  // ora esclude le cancellate.
  const user = useAppSelector((state) => selectGetUserResult(state, userId));
  const { data: historyData } = useGetUserWorkoutHistoryQuery(userId ?? "", {
    skip: !userId,
  });
  const [hardDeleteWorkout, { isLoading: isDeleting }] =
    useHardDeleteWorkoutMutation();
  const [triggerGetWorkout] = useLazyGetWorkoutQuery();
  const navigate = useNavigate();

  // Apre la pagina di dettaglio scheda direttamente in edit mode.
  // WorkoutDetails legge ?edit=1 e attiva l'edit toggle solo dopo che il
  // workout e' caricato. Funziona anche per schede soft-deleted: GET
  // /workout/{id} non filtra deletedAt e l'update preserva il flag, quindi
  // una scheda cancellata puo' essere corretta senza "resuscitare".
  // Per le cancellate chiediamo conferma esplicita: il PT deve sapere che
  // le modifiche non saranno visibili al cliente finche' non viene
  // ripristinata (banner mostrato in WorkoutDetails).
  // Roadmap: in futuro lo Storico avra' "Assegna ad altro utente" e
  // "Ripristina"; oggi non sono esposti.
  const handleEdit = (item: WorkoutHistoryItem) => {
    if (item.deletedAt != null) {
      const ok = window.confirm(
        "Questa scheda è cancellata. Le modifiche saranno salvate ma " +
          "non saranno visibili al cliente finché non verrà ripristinata.\n\n" +
          "Vuoi continuare?"
      );
      if (!ok) return;
    }
    const path = generatePath(ROUTES.WORKOUT_DETAILS, { id: item.id });
    navigate(`${path}?edit=1`);
  };

  const [toDelete, setToDelete] = useState<WorkoutHistoryItem | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Modale di visualizzazione
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewWorkout, setViewWorkout] = useState<Workout | null>(null);

  // Render nascosto per la cattura screenshot → PDF
  const [captureState, setCaptureState] = useState<{
    workout: Workout;
    fileName: string;
  } | null>(null);
  const captureRef = useRef<HTMLDivElement>(null);

  const userName = [user?.name, user?.surname].filter(Boolean).join(" ");
  const numericUserId = userId ? Number(userId) : undefined;

  const workouts = [...(historyData ?? [])].sort(sortByCreatedAtDesc);

  // Apertura modale: scarica la scheda completa e la mostra come in dashboard.
  const handleView = async (item: WorkoutHistoryItem) => {
    setViewOpen(true);
    setViewLoading(true);
    setViewWorkout(null);
    try {
      const workout = await triggerGetWorkout(item.id).unwrap();
      setViewWorkout(workout);
    } catch (e) {
      console.error(e);
      alert("Errore durante il caricamento della scheda");
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  // Download PDF: scarica la scheda, la renderizza in un container nascosto e poi
  // (useEffect su captureState) cattura uno screenshot per giorno.
  const handleDownload = async (item: WorkoutHistoryItem) => {
    setDownloadingId(item.id);
    try {
      const workout = await triggerGetWorkout(item.id).unwrap();
      const fileName =
        sanitizeFileName(item.name || workout.name || "scheda") +
        (item.createdAt ? "_" + new Date(item.createdAt).toISOString().slice(0, 10) : "") +
        ".pdf";
      setCaptureState({ workout, fileName });
    } catch (e) {
      console.error(e);
      alert("Errore durante la generazione del PDF");
      setDownloadingId(null);
    }
  };

  // Quando il render nascosto è montato, cattura i giorni e genera il PDF.
  useEffect(() => {
    if (!captureState) return;
    let cancelled = false;
    const run = async () => {
      // Attendi un paio di frame + font/immagini, così il layout è pronto.
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      await new Promise((r) => setTimeout(r, 450));
      try {
        await (document as unknown as { fonts?: { ready: Promise<unknown> } })
          .fonts?.ready;
      } catch {}
      const container = captureRef.current;
      if (cancelled || !container) return;
      const headerNode = container.querySelector<HTMLElement>("[data-workout-header]");
      const dayNodes = Array.from(
        container.querySelectorAll<HTMLElement>("[data-workout-day]")
      );
      const allNodes = [
        ...(headerNode ? [headerNode] : []),
        ...dayNodes,
      ];
      try {
        if (dayNodes.length === 0) {
          alert("Questa scheda non ha giorni da esportare.");
        } else {
          await captureElementsToPdf(allNodes, captureState.fileName);
        }
      } catch (e) {
        console.error(e);
        alert("Errore durante la generazione del PDF");
      } finally {
        if (!cancelled) {
          setCaptureState(null);
          setDownloadingId(null);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [captureState]);

  const handleConfirmDelete = () => {
    if (!toDelete) return;
    // Lo Storico cancella per davvero (hard delete): e' l'unico posto da cui
    // una scheda puo' essere rimossa fisicamente dal DB.
    hardDeleteWorkout({ id: Number(toDelete.id) })
      .unwrap()
      .then(() => setToDelete(null))
      .catch(() => {});
  };

  // Larghezza del container nascosto: più settimane = più largo (le settimane
  // sono colonne affiancate dentro ogni giorno).
  const captureWeeks = captureState?.workout.data?.length || 1;
  const captureWidth = Math.min(1800, Math.max(1100, 380 + captureWeeks * 220));

  if (workouts.length === 0) {
    return (
      <div className="text-xs text-gray-700 uppercase dark:text-gray-400 mt-8">
        Non ci sono schede associate a questo utente
      </div>
    );
  }

  return (
    <>
      <div className="relative overflow-x-auto shadow-md sm:rounded-lg my-8">
        <table className="w-full text-sm text-gray-500 dark:text-gray-400 text-center">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr className="text-md">
              <th scope="col" className="px-6 py-3 text-center">
                Data creazione
              </th>
              <th scope="col" className="px-6 py-3 text-center">
                Nome
              </th>
              <th scope="col" className="px-6 py-3 text-center">
                Tipo
              </th>
              <th scope="col" className="px-6 py-3 text-center">
                Azioni
              </th>
            </tr>
          </thead>
          <tbody>
            {workouts.map((w, index) => (
              <tr
                key={w.id}
                onClick={() => handleView(w)}
                title="Clicca per visualizzare la scheda"
                className={`${
                  index % 2 !== 0
                    ? "bg-gray-50 dark:bg-gray-700"
                    : "bg-white dark:bg-gray-900"
                } dark:border-gray-700 cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors`}
              >
                <th
                  scope="row"
                  className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                >
                  {formatDate(w.createdAt)}
                </th>
                <td className="px-6 py-4">
                  <div className="inline-flex items-center gap-2">
                    <span>{w.name || "Scheda"}</span>
                    {w.deletedAt != null && (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200"
                        title={`Cancellata il ${formatDate(w.deletedAt)}`}
                      >
                        Cancellata
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {w.workoutType === "TEMPLATE" ? "Template" : "Scheda"}
                </td>
                <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleEdit(w)}
                      disabled={downloadingId === w.id}
                      title="Modifica scheda"
                      aria-label="Modifica scheda"
                      className="inline-flex items-center hover:bg-amber-50 dark:hover:bg-gray-700 focus:ring-4 focus:outline-none focus:ring-amber-300 font-medium rounded-lg text-sm px-3 py-2.5 text-center dark:focus:ring-amber-900 disabled:opacity-50"
                    >
                      <svg
                        aria-hidden="true"
                        className="w-5 h-5 fill-amber-600 hover:fill-amber-700 dark:fill-amber-500 dark:hover:fill-amber-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                        <path
                          fillRule="evenodd"
                          d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownload(w)}
                      disabled={downloadingId === w.id}
                      title="Scarica PDF"
                      aria-label="Scarica PDF"
                      className="inline-flex items-center text-white hover:bg-blue-50 dark:hover:bg-gray-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-2.5 text-center dark:focus:ring-blue-900 disabled:opacity-50"
                    >
                      {downloadingId === w.id ? (
                        <svg
                          className="animate-spin h-5 w-5 text-blue-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12z"
                          ></path>
                        </svg>
                      ) : (
                        <svg
                          aria-hidden="true"
                          className="w-5 h-5 fill-blue-600 hover:fill-blue-700 dark:fill-blue-500 dark:hover:fill-blue-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm6.293-2.293a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L11 11.586V3a1 1 0 10-2 0v8.586L6.707 9.293a1 1 0 00-1.414 1.414l4 4z"
                            clipRule="evenodd"
                          ></path>
                        </svg>
                      )}
                    </button>
                    <span aria-label="Elimina definitivamente dallo storico">
                      <DeleteButton
                        variant="icon"
                        onClick={() => setToDelete(w)}
                      />
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <WorkoutViewModal
        open={viewOpen}
        loading={viewLoading}
        workout={viewWorkout}
        userName={userName || undefined}
        userId={numericUserId}
        onClose={() => {
          setViewOpen(false);
          setViewWorkout(null);
        }}
      />

      {/* Render nascosto usato solo per catturare gli screenshot dei giorni */}
      {captureState && (
        <div
          aria-hidden
          ref={captureRef}
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            zIndex: -1,
            width: captureWidth,
            background: "#111827",
            padding: 16,
            pointerEvents: "none",
          }}
        >
          <div data-workout-header style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: "24px 24px 16px 24px",
            marginBottom: 16,
            background: "#111827",
          }}>
            <img
              src="/logo-fitshape.jpg"
              alt="FitShape.Lab"
              style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 8 }}
              crossOrigin="anonymous"
            />
            <div style={{ fontSize: 20, fontWeight: 700, color: "#ffffff" }}>
              {userName || captureState.workout.name || "Scheda"}
            </div>
          </div>
          <WorkoutTables workout={captureState.workout} userId={numericUserId} />
        </div>
      )}

      <Modal
        title="Elimina definitivamente"
        openModal={toDelete ? "deleteWorkout" : undefined}
        setOpenModal={() => setToDelete(null)}
      >
        <p className="mb-4 text-gray-600 dark:text-gray-300">
          Vuoi eliminare <strong>definitivamente</strong> la scheda{" "}
          <strong>{toDelete?.name || "Scheda"}</strong> dallo storico?
          L&apos;operazione non è reversibile e la scheda verrà rimossa anche
          dal database.
        </p>
        <div className="flex gap-3">
          {/* Bottone custom: il DeleteButton di default mostra "Delete" in
              inglese, qui serve testo italiano coerente con il resto del
              modale e piu' esplicito sull'irreversibilita'. */}
          <button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:ring-4 focus:outline-none focus:ring-red-300 rounded-lg dark:bg-red-700 dark:hover:bg-red-800 dark:focus:ring-red-900 disabled:opacity-50"
          >
            {isDeleting ? "Eliminazione…" : "Elimina definitivamente"}
          </button>
          <button
            onClick={() => setToDelete(null)}
            disabled={isDeleting}
            className="px-4 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            Annulla
          </button>
        </div>
      </Modal>
    </>
  );
};

function sortByCreatedAtDesc(
  a: { id: string; createdAt?: number | null },
  b: { id: string; createdAt?: number | null }
): number {
  // Più recenti in alto; le schede legacy (createdAt null) vanno in fondo,
  // tra loro ordinate per id.
  if (a.createdAt != null && b.createdAt != null) return b.createdAt - a.createdAt;
  if (a.createdAt != null) return -1;
  if (b.createdAt != null) return 1;
  return Number(b.id) - Number(a.id);
}

function formatDate(ts?: number | null): string {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleDateString("it-IT");
  } catch {
    return "—";
  }
}
